// Relative import: this module is also compiled by the server tsconfig, which has no `~` alias.
import type {ListVariation} from '../types'

/**
 * LEVANTA, its LEVANTA_V2 sandbox, the CC (Creator Connections) lists and the VERDICT page share every
 * Levanta page rule (no sponsored, no related lists, no DWS dictionary, no brand cap, hard 404 without a
 * ranking, noindex). CC products carry no levantaLink, so their buttons stay plain Amazon links; a VERDICT
 * page decides the link per product (see verdict-link.ts).
 */
export function isLevantaVariation(variation: ListVariation | undefined): boolean {
  return (
    variation === 'LEVANTA' ||
    variation === 'LEVANTA_V2' ||
    variation === 'CC' ||
    variation === 'VERDICT' ||
    variation === 'VERDICT_V2'
  )
}

/**
 * The one-hero VERDICT page under /pick/ and its VERDICT_V2 build sandbox under /pick2/ (the same page on the
 * sandbox rows). Only these render the verdict layout and the per-product link rule.
 */
export function isVerdictVariation(variation: ListVariation | undefined): boolean {
  return variation === 'VERDICT' || variation === 'VERDICT_V2'
}

/**
 * Route names of the list pages served under a path prefix (/lv/, /lv2/, /cc/ → levanta_slug_page;
 * /pick/, /pick2/ → pick_slug_page). They are list pages whose slug is the path minus the prefix, and they are never indexed.
 */
export const PREFIXED_LIST_ROUTE_NAMES = ['levanta_slug_page', 'pick_slug_page'] as const

export function isPrefixedListRoute(routeName: string | undefined): boolean {
  return (PREFIXED_LIST_ROUTE_NAMES as readonly string[]).includes(routeName ?? '')
}

/** The path prefix of every prefixed list route; stripping it yields the catalog slug path. */
export const LIST_PATH_PREFIX = /^\/(lv2?|cc|pick2?)\//

/**
 * Hosts that promote only the /cc/ (Creator Connections) lists: a /cc/<slug> page is indexed, a plain
 * /<slug> list is noindex and points its canonical at /cc/<slug>. Adding a host is one line here.
 * Every other host (and every other locale of the same brand) keeps the default rules.
 */
export const CC_ONLY_HOSTS = ['www.bestexpertschoice.com'] as const

export function isCcOnlyHost(host: string | undefined): boolean {
  const hostname = (host ?? '').toLowerCase().split(':')[0]
  return (CC_ONLY_HOSTS as readonly string[]).includes(hostname)
}

/**
 * Canonical (and og:url) of a list page. By default every variation points at the plain /<slug> list.
 * On a CC-only host the CC list is the page we promote, so both /cc/<slug> and the plain /<slug> point at
 * /cc/<slug>. The plain page does this without checking that a CC list exists: it is noindex on that host
 * either way, so a canonical to a missing /cc/ page changes nothing in search.
 */
export function listCanonicalUrl({
  domain,
  slug,
  variation,
}: {
  domain: string | undefined
  slug: string | undefined
  variation: ListVariation | undefined
}): string {
  const effectiveVariation = variation ?? 'BASELINE'
  if (slug && isCcOnlyHost(domain) && (effectiveVariation === 'CC' || effectiveVariation === 'BASELINE')) {
    return `https://${domain}/cc/${slug}`
  }

  return `https://${domain}/${slug}`
}
